import { useState } from "react";
import { finalizePartnerPayment, money, type PartnerUpcomingItem } from "@/lib/partnerPayments";
import { toast } from "sonner";

interface Props {
  item: PartnerUpcomingItem;
  onClose: () => void;
  onFinalized: () => void;
}

export function PartnerPaymentReviewDialog({ item, onClose, onFinalized }: Props) {
  const calc = item.calculation;
  const [paymentDate, setPaymentDate] = useState<string>(item.period.due_date);
  const [method, setMethod] = useState<string>(item.payee.default_payment_method ?? "check");
  const [reference, setReference] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [receipt, setReceipt] = useState<string>("");
  const [saving, setSaving] = useState(false);

  async function submit() {
    setSaving(true);
    try {
      await finalizePartnerPayment({
        agreement_id: item.agreement_id,
        period_start: item.period.start,
        period_end: item.period.end,
        payment_date: paymentDate,
        payment_method: method,
        reference: reference || undefined,
        notes: notes || undefined,
        receipt_url: receipt || undefined,
      });
      toast.success(`${item.payee.name} payment recorded`);
      onFinalized();
    } catch (e: any) {
      toast.error(e?.message ?? "Could not record payment");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10 px-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-lg bg-card border border-border shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-border/60 flex items-center justify-between">
          <div>
            <h3 className="font-serif text-lg text-foreground">{item.payee.name} — {item.period.label} Payment</h3>
            <p className="text-xs text-muted-foreground">Covers {item.period.start} → {item.period.end}</p>
          </div>
          <button onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground">Close</button>
        </div>

        <div className="p-5 space-y-5 text-sm">
          <div className="rounded-md bg-muted/30 p-3">
            <p><strong>Reporting cutoff:</strong> {calc.reporting_cutoff}</p>
            <p><strong>Percentage:</strong> {calc.percentage}%</p>
            <p><strong>Registrations included:</strong> {calc.included_payment_ids.length}</p>
          </div>

          <div>
            <p className="font-semibold text-foreground mb-2">Per-class subtotals</p>
            <ul className="rounded-md border border-border/60 divide-y divide-border/50">
              {calc.per_class.map((c) => (
                <li key={c.class_id} className="flex items-center justify-between px-3 py-2">
                  <span className="truncate">{c.class_id} <span className="text-xs text-muted-foreground">· {c.registration_count} regs</span></span>
                  <span className="font-medium">{money(c.net_cents)}</span>
                </li>
              ))}
              {calc.per_class.length === 0 && <li className="px-3 py-3 text-muted-foreground">No qualifying revenue in this period yet.</li>}
            </ul>
          </div>

          {calc.excluded_payments.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground">Excluded payments ({calc.excluded_payments.length})</summary>
              <ul className="mt-2 space-y-1 max-h-40 overflow-auto">
                {calc.excluded_payments.map((e) => (
                  <li key={e.id}>{e.class_id} — {e.reason} — {money(e.amount_paid)}</li>
                ))}
              </ul>
            </details>
          )}

          <div className="rounded-md bg-muted/30 p-3 space-y-1">
            <p className="flex justify-between"><span>Gross qualifying revenue</span><span>{money(calc.gross_qualifying_revenue_cents)}</span></p>
            <p className="flex justify-between"><span>Refunds / adjustments</span><span>−{money(calc.refund_adjustments_cents)}</span></p>
            <p className="flex justify-between font-medium"><span>Net qualifying revenue</span><span>{money(calc.net_qualifying_revenue_cents)}</span></p>
            <p className="flex justify-between text-base font-semibold pt-1 border-t border-border/60"><span>{calc.percentage}% due</span><span>{money(calc.amount_due_cents)}</span></p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs">
              Payment date
              <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} className="mt-1 w-full h-9 px-2 rounded border border-border bg-background" />
            </label>
            <label className="text-xs">
              Method
              <select value={method} onChange={(e) => setMethod(e.target.value)} className="mt-1 w-full h-9 px-2 rounded border border-border bg-background">
                <option value="check">Check</option>
                <option value="cash">Cash</option>
                <option value="ach">ACH / bank transfer</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="text-xs col-span-2">
              Check / reference #
              <input value={reference} onChange={(e) => setReference(e.target.value)} className="mt-1 w-full h-9 px-2 rounded border border-border bg-background" />
            </label>
            <label className="text-xs col-span-2">
              Notes
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 w-full px-2 py-1 rounded border border-border bg-background" />
            </label>
            <label className="text-xs col-span-2">
              Receipt URL (optional)
              <input value={receipt} onChange={(e) => setReceipt(e.target.value)} className="mt-1 w-full h-9 px-2 rounded border border-border bg-background" />
            </label>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-border/60 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-3 py-2 rounded border border-border">Cancel</button>
          <button
            onClick={submit}
            disabled={saving || calc.amount_due_cents === 0}
            className="text-sm px-3 py-2 rounded bg-primary text-primary-foreground disabled:opacity-50"
          >
            {saving ? "Saving…" : `Confirm & Record ${money(calc.amount_due_cents)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
